import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Bell, Check, Trash2, Download, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function NotificationBell() {
  const [count, setCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const prevCountRef = useRef(null);
  const notifiedIdsRef = useRef(new Set());

  // Richiedi permesso notifiche browser al mount (se non già deciso)
  useEffect(() => {
    if (typeof window !== "undefined" && "Notification" in window) {
      if (Notification.permission === "default") {
        Notification.requestPermission().catch(() => {});
      }
    }
  }, []);

  const triggerBrowserPush = async () => {
    if (!("Notification" in window) || Notification.permission !== "granted") return;
    try {
      // Recupera l'ultima notifica non letta per il messaggio
      const res = await axios.get(
        `${API}/notifications?tipo=documento_firmato,documento_personale,appuntamento_cancellato,appuntamento_riprogrammato`,
        { withCredentials: true }
      );
      const unread = (res.data || []).filter((n) => !n.letta);
      if (unread.length === 0) return;
      // Prendi solo quelle non ancora notificate in questa sessione browser
      const nuove = unread.filter((n) => !notifiedIdsRef.current.has(n.id));
      if (nuove.length === 0) return;
      // Mostra fino a 3 push per evitare spam
      nuove.slice(0, 3).forEach((n) => {
        try {
          const push = new Notification(n.titolo || "Documento firmato ricevuto", {
            body: n.messaggio || `${n.cliente_nome || ""} ha caricato documenti firmati`,
            icon: "/favicon.ico",
            tag: `doc-firmato-${n.id}`,
            requireInteraction: false,
          });
          push.onclick = () => {
            window.focus();
            setOpen(true);
            push.close();
          };
          notifiedIdsRef.current.add(n.id);
        } catch (e) {
          console.warn("Push notification failed:", e);
        }
      });
    } catch (e) {
      console.error("triggerBrowserPush error:", e);
    }
  };

  const fetchCount = async () => {
    try {
      const res = await axios.get(`${API}/notifications/count?tipo=documento_firmato,documento_personale,appuntamento_cancellato,appuntamento_riprogrammato`, { withCredentials: true });
      const newCount = res.data.count;
      // Se il contatore è cresciuto rispetto al precedente valore noto, invia push
      if (prevCountRef.current !== null && newCount > prevCountRef.current) {
        triggerBrowserPush();
      }
      prevCountRef.current = newCount;
      setCount(newCount);
    } catch (error) {
      console.error("Error fetching notification count:", error);
    }
  };

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/notifications?tipo=documento_firmato,documento_personale,appuntamento_cancellato,appuntamento_riprogrammato`, { withCredentials: true });
      setNotifications(res.data);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(
        `${API}/notifications/${notificationId}`,
        { letta: true },
        { withCredentials: true }
      );
      fetchNotifications();
      fetchCount();
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.post(`${API}/notifications/mark-all-read?tipo=documento_firmato,documento_personale,appuntamento_cancellato,appuntamento_riprogrammato`, {}, { withCredentials: true });
      fetchNotifications();
      fetchCount();
      toast.success("Tutte le notifiche segnate come lette");
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await axios.delete(`${API}/notifications/${notificationId}`, { withCredentials: true });
      fetchNotifications();
      fetchCount();
      toast.success("Notifica eliminata");
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const downloadFile = async (notificationId, filename) => {
    try {
      const response = await axios.get(
        `${API}/notifications/${notificationId}/download/${encodeURIComponent(filename)}`,
        { 
          withCredentials: true,
          responseType: 'blob'
        }
      );
      
      // Crea un link temporaneo per il download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Download di ${filename} completato`);
    } catch (error) {
      console.error("Error downloading file:", error);
      toast.error("Errore nel download del file");
    }
  };

  useEffect(() => {
    fetchCount();
    // Poll ogni 30 secondi
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (open) {
      fetchNotifications();
    }
  }, [open]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Proprio ora";
    if (diffMins < 60) return `${diffMins} minuti fa`;
    if (diffHours < 24) return `${diffHours} ore fa`;
    if (diffDays < 7) return `${diffDays} giorni fa`;
    return date.toLocaleDateString('it-IT');
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="relative p-2 hover:bg-white/10 rounded-lg transition-colors">
          <Bell className="w-5 h-5 text-white" />
          {count > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
              {count > 9 ? '9+' : count}
            </span>
          )}
        </button>
      </DialogTrigger>

      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Notifiche
            </DialogTitle>
            {notifications.filter(n => !n.letta).length > 0 && (
              <Button
                size="sm"
                variant="outline"
                onClick={markAllAsRead}
                className="text-xs"
              >
                <Check className="w-4 h-4 mr-1" />
                Segna tutte come lette
              </Button>
            )}
          </div>
        </DialogHeader>

        <div className="space-y-3">
          {loading ? (
            <div className="text-center py-8">
              <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="text-slate-600 mt-2">Caricamento...</p>
            </div>
          ) : notifications.length === 0 ? (
            <div className="text-center py-8">
              <Bell className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600">Nessuna notifica</p>
            </div>
          ) : (
            notifications.map((notification) => (
              <Card
                key={notification.id}
                className={`${
                  !notification.letta
                    ? "border-blue-300 bg-blue-50"
                    : "border-slate-200"
                }`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-1">
                        <h4 className="font-semibold text-sm">
                          {notification.titolo}
                        </h4>
                        {!notification.letta && (
                          <span className="px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full">
                            Nuova
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-700 mb-2">
                        {notification.messaggio}
                      </p>
                      
                      {/* Sezione download file */}
                      {notification.files && notification.files.length > 0 && (
                        <div className="mt-3 p-2 bg-slate-100 rounded-lg">
                          <p className="text-xs font-medium text-slate-600 mb-2 flex items-center gap-1">
                            <FileText className="w-3 h-3" />
                            Documenti allegati:
                          </p>
                          <div className="space-y-1">
                            {notification.files.map((filename, idx) => (
                              <button
                                key={`${notification.id}-${filename}-${idx}`}
                                onClick={() => downloadFile(notification.id, filename)}
                                className="flex items-center gap-2 text-xs text-blue-600 hover:text-blue-800 hover:underline w-full text-left p-1 rounded hover:bg-blue-50"
                              >
                                <Download className="w-3 h-3 flex-shrink-0" />
                                <span className="truncate">{filename}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      <p className="text-xs text-slate-500 mt-2">
                        {formatDate(notification.data_creazione)}
                      </p>
                    </div>
                    <div className="flex gap-1">
                      {!notification.letta && (
                        <button
                          onClick={() => markAsRead(notification.id)}
                          className="p-1 hover:bg-slate-200 rounded"
                          title="Segna come letta"
                        >
                          <Check className="w-4 h-4 text-green-600" />
                        </button>
                      )}
                      <button
                        onClick={() => deleteNotification(notification.id)}
                        className="p-1 hover:bg-slate-200 rounded"
                        title="Elimina"
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
